// [C28] Roles editor (D-034: the Super Manager adds or removes permissions on any role and creates new roles). New —
// the old app had no roles screen (roles were three fixed names, UsersCard header note); built in the frame of the
// user dialog extracted from original/src/App.tsx:57529-57800 (same header strip, labels, grouped permission grid,
// own-PIN block, submit button) so it reads as part of the same card. Deviation V-58.
import { motion } from 'motion/react';
import { AlertTriangle, Shield, ShieldPlus, X } from 'lucide-react';
import type { PermissionDef } from '@faylasuf/contracts';
import { useI18n } from '../i18n-context.js';

export interface RoleFormValues {
  readonly name: string;
  readonly permissions: readonly string[];
}

export interface RoleDialogProps {
  readonly mode: 'create' | 'edit';
  readonly values: RoleFormValues;
  readonly onChange: (patch: Partial<RoleFormValues>) => void;
  readonly permissionCatalogue: readonly PermissionDef[];
  /** Edit: active users whose sessions end when the role changes (D-229 (d)). */
  readonly activeUsers: number;
  readonly builtin: boolean;
  readonly pin: string;
  readonly onPinChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  readonly error: string | null;
  readonly busy: boolean;
}

const LABEL = 'block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest';
const GROUP_ORDER: readonly PermissionDef['group'][] = ['sections', 'dashboard_cards', 'movements', 'customers', 'money', 'custody', 'settings', 'users', 'audit'];

export function RoleDialog(props: RoleDialogProps) {
  const { t, locale } = useI18n();
  const v = props.values;
  const dir = locale === 'ar' ? 'rtl' : 'ltr';
  const isEdit = props.mode === 'edit';
  const groups = GROUP_ORDER.map((g) => ({ group: g, items: props.permissionCatalogue.filter((p) => p.group === g) })).filter((g) => g.items.length > 0);
  const toggleGroup = (tokens: readonly string[], on: boolean) => {
    const rest = v.permissions.filter((p) => !tokens.includes(p));
    props.onChange({ permissions: on ? [...rest, ...tokens] : rest });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="role-dialog">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={props.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-lg rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border flex flex-col max-h-[90vh]"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-blue-50/50 dark:bg-blue-900/10">
          <h3 className="text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow">
            {isEdit ? <Shield size={24} className="text-blue-500" /> : <ShieldPlus size={24} className="text-blue-500" />}
            {isEdit ? t('roles.edit_title') : t('roles.add_new')}
          </h3>
          <button type="button" onClick={props.onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors cursor-pointer">
            <X size={24} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!props.busy) props.onSubmit();
          }}
          className="p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1 text-right"
          dir={dir}
        >
          <div>
            <label className={LABEL}>{t('roles.name')}</label>
            <input
              required
              type="text"
              data-testid="role-name"
              value={v.name}
              maxLength={60}
              onChange={(e) => props.onChange({ name: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-black text-right"
            />
            {props.builtin && <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 mt-1">{t('roles.builtin_hint')}</p>}
          </div>
          <div>
            <label className={LABEL}>
              {t('users.permissions')} · {t('users.permissions_count', { n: v.permissions.length })}
            </label>
            <div className="space-y-3">
              {groups.map((g) => {
                const tokens = g.items.map((p) => p.token);
                const all = tokens.every((tk) => v.permissions.includes(tk));
                return (
                  <div key={g.group}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] font-black text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">{t(`perm.group.${g.group}`)}</p>
                      <button type="button" data-testid={`role-group-${g.group}`} onClick={() => toggleGroup(tokens, !all)} className="text-[10px] font-black text-blue-600 dark:text-blue-400 hover:underline">
                        {all ? t('roles.clear_group') : t('roles.select_group')}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {g.items.map((perm) => {
                        const checked = v.permissions.includes(perm.token);
                        return (
                          <label key={perm.token} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-[#05070a] rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-[#161b22] transition-colors border border-transparent dark:border-[#30363d]">
                            <input
                              type="checkbox"
                              checked={checked}
                              data-testid={`role-perm-${perm.token}`}
                              onChange={(e) => props.onChange({ permissions: e.target.checked ? [...v.permissions, perm.token] : v.permissions.filter((p) => p !== perm.token) })}
                              className="w-4 h-4 rounded text-blue-600 dark:bg-[#0d1117] dark:border-[#30363d]"
                            />
                            <span className="text-[10px] font-black dark:text-[#f0f6fc] text-right">{locale === 'ar' ? perm.ar : perm.en}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {isEdit && props.activeUsers > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 text-amber-700 dark:text-amber-400" data-testid="role-sessions-warning">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <p className="text-[11px] font-bold leading-relaxed">{t('roles.sessions_warning', { n: props.activeUsers })}</p>
            </div>
          )}
          <div className="p-4 bg-rose-50/50 dark:bg-rose-950/10 rounded-2xl border border-rose-100/50 dark:border-rose-900/20 space-y-3">
            <span className="text-xs font-bold text-rose-600 dark:text-rose-400 block text-right">{t('users.pin_confirm')}</span>
            <input
              required
              type="password"
              inputMode="numeric"
              maxLength={6}
              autoComplete="off"
              data-testid="role-pin"
              value={props.pin}
              onChange={(e) => props.onPinChange(e.target.value)}
              placeholder={t('users.pin_placeholder')}
              className="w-full bg-slate-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 dark:text-[#f0f6fc] font-mono text-center text-lg font-bold tracking-widest transition-all"
            />
          </div>
          {props.error && <p data-testid="role-dialog-error" className="text-xs font-bold text-rose-500 text-center">{props.error}</p>}
          <button
            type="submit"
            disabled={props.busy}
            data-testid="role-submit"
            className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all cursor-pointer disabled:opacity-50"
          >
            {isEdit ? t('users.save') : t('roles.create')}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
